import isNil from 'lodash.isnil'
import omit from 'lodash.omit'
import { makeAutoObservable, toJS } from 'mobx'
import {
  ViewFilter,
  ViewFilterDateFragment,
  ViewFilterFragment,
  ViewFilterMembershipType,
  ViewFilterNumberComparison,
  ViewFilterOptionFragment,
} from 'types/graphql'

import { FileOnRecordTypeDisplayMap } from 'src/constants'
import { TableColumnTemplate } from 'src/models/TableViews/TableColumnTemplate'
import { TableViewColumnTemplateStore } from 'src/models/TableViews/TableViewColumnTemplateStore'
import { TableViewDateFilter } from 'src/models/TableViews/TableViewDateFilter'
import { RecordTaskGroupTypeToRecordTaskGroupNameMap, formatUserName } from 'src/utils'
import { NumberFormat } from 'src/utils/number'

import {
  RecordTaskStatusTypeDisplayMap,
  RecordTaskTypeDisplayMap,
  ViolationStatusToDisplayMap,
} from '../../constants'
import { TransferTypeGovwellToDisplayMap } from '../../hooks/filter-inputs/use-payment-filter-inputs'
import { formatPaymentMethod } from '../../utils/fee'

const EMPTY_DATE_PROTOCOL: ViewFilterDateFragment = { type: 'Exact' }

const DEFAULT_MEMBERSHIP_TYPE: ViewFilterMembershipType = 'IsIn'

/** True when RecordTypeStatus filter options include statuses from 2+ modules. */
export const recordTypeStatusOptionsSpanMultipleModules = (
  filterOptions: ViewFilterOptionFragment[]
): boolean => {
  const recordTypeIds = new Set<number>()
  for (const option of filterOptions) {
    if (option.data.__typename !== 'ViewDataElementRecordTypeStatus') {
      continue
    }
    const recordTypeId = option.data.recordTypeStatus?.recordType?.id
    if (recordTypeId != null) {
      recordTypeIds.add(recordTypeId)
    }
  }
  return recordTypeIds.size > 1
}

type ViewFilterWithoutColumnType = Omit<ViewFilter, 'columnType'>

export class TableViewFilter {
  public readonly columnTemplateGeneratedId: string

  public optionsSelected: string[] = []
  public membershipType: ViewFilterMembershipType = DEFAULT_MEMBERSHIP_TYPE
  public bool: boolean | null | undefined
  public date: TableViewDateFilter = new TableViewDateFilter(EMPTY_DATE_PROTOCOL)
  public isNull: boolean | null | undefined
  public number: number | null | undefined
  public numberComparison: ViewFilterNumberComparison | null | undefined

  private _columnTemplateStore: TableViewColumnTemplateStore

  // This value should always get overwritten in the constructor via _setProtocolValues, just satisfying initializer requirement
  private _databaseValue: ViewFilterWithoutColumnType = {
    columnTemplateGeneratedId: 'Record',
  }

  constructor(args: {
    protocol: ViewFilterWithoutColumnType
    columnTemplateStore: TableViewColumnTemplateStore
  }) {
    const { protocol, columnTemplateStore } = args
    this._columnTemplateStore = columnTemplateStore
    this.columnTemplateGeneratedId = protocol.columnTemplateGeneratedId
    this._initializeValues(protocol)
    makeAutoObservable(this)
  }

  private _initializeValues(protocol: ViewFilterWithoutColumnType) {
    this.setValues(protocol)
    this.save()
  }

  public setValues(protocol: ViewFilterWithoutColumnType) {
    this.optionsSelected = protocol.membership?.optionsSelected ?? []
    this.membershipType = protocol.membership?.type ?? DEFAULT_MEMBERSHIP_TYPE
    this.bool = protocol.bool ?? undefined
    this.isNull = protocol.isNull ?? undefined
    this.date = new TableViewDateFilter(
      protocol.date ? omit(protocol.date, '__typename') : EMPTY_DATE_PROTOCOL
    )
    this.number = protocol.number?.number
    this.numberComparison = protocol.number?.comparison
  }

  public get columnTemplate(): TableColumnTemplate | undefined {
    const template = this._columnTemplateStore.getColumnTemplateByGeneratedId(
      this.columnTemplateGeneratedId
    )
    if (!template) {
      return undefined
    }
    return TableColumnTemplate.fromProtocol(template)
  }

  public get numberFormat(): NumberFormat {
    return this.columnTemplate?.dataType === 'CurrencyCents'
      ? NumberFormat.Currency
      : NumberFormat.Float
  }

  public get hasUnsavedChanges() {
    return !this._equals(this._databaseValue)
  }

  public get isPopulated(): boolean {
    return TableViewFilter.isFilterPopulated(this.protocol)
  }

  public clear() {
    this.optionsSelected = []
    this.membershipType = DEFAULT_MEMBERSHIP_TYPE
    this.isNull = undefined
    this.bool = undefined
    this.date = new TableViewDateFilter(EMPTY_DATE_PROTOCOL)
    this.number = undefined
    this.numberComparison = undefined
  }

  public clone(): TableViewFilter {
    return new TableViewFilter({
      columnTemplateStore: this._columnTemplateStore,
      protocol: this.protocol,
    })
  }

  private _equals(otherFilterProtocol: ViewFilterWithoutColumnType): boolean {
    const thisOptionsSelected = this.optionsSelected.slice().sort().join()
    const otherOptionsSelected = (otherFilterProtocol.membership?.optionsSelected ?? [])
      .slice()
      .sort()
      .join()

    return (
      this.protocol.bool === otherFilterProtocol.bool &&
      this.protocol.isNull === otherFilterProtocol.isNull &&
      this.date.equals(otherFilterProtocol.date) &&
      this.number === otherFilterProtocol.number?.number &&
      this.numberComparison === otherFilterProtocol.number?.comparison &&
      this.membershipType === (otherFilterProtocol.membership?.type ?? DEFAULT_MEMBERSHIP_TYPE) &&
      thisOptionsSelected === otherOptionsSelected
    )
  }

  public equals(otherFilter: TableViewFilter): boolean {
    return this._equals(otherFilter.protocol)
  }

  public static isFilterPopulated(protocol: ViewFilterFragment | undefined) {
    return (
      !!protocol &&
      (!isNil(protocol.bool) ||
        !isNil(protocol.isNull) ||
        !!protocol.membership?.optionsSelected?.length ||
        TableViewDateFilter.isDatePopulated(protocol.date) ||
        !isNil(protocol.number))
    )
  }

  private _getTemplateOptionForOptionId(id: string): ViewFilterOptionFragment | undefined {
    return this.columnTemplate?.filterOptions?.find((o) => o.identifier === id)
  }

  public getOptionDisplayValue(id: string, paymentProviderFormatted: string): string {
    const filterOption = this._getTemplateOptionForOptionId(id)
    if (!filterOption) {
      return ''
    }

    const data = filterOption.data
    switch (data.__typename) {
      case 'ViewDataElementRecordActionRequiredBy':
        return data.recordActionRequiredBy
      case 'ViewDataElementRecordTaskGroupType':
        return RecordTaskGroupTypeToRecordTaskGroupNameMap[data.recordTaskGroupType]
      case 'ViewDataElementRecordTemplate':
        return data.recordTemplate.name ?? ''
      case 'ViewDataElementRecordTypeStatus': {
        const statusName = data.recordTypeStatus?.name ?? ''
        const moduleName = data.recordTypeStatus?.recordType?.displayName
        const filterOptions = this.columnTemplate?.filterOptions ?? []
        if (moduleName && recordTypeStatusOptionsSpanMultipleModules(filterOptions)) {
          return `${statusName} (${moduleName})`
        }
        return statusName
      }
      case 'ViewDataElementString':
        return data.string
      case 'ViewDataElementUser':
        return formatUserName({
          firstName: data.user.firstName,
          lastName: data.user.lastName,
        })
      case 'ViewDataElementViolationStatus': {
        return ViolationStatusToDisplayMap[data.violationStatus]
      }
      case 'ViewDataElementViolationType': {
        return data.violationType.name
      }
      case 'ViewDataElementInspectionTemplate': {
        return data.inspectionTemplate.name ?? ''
      }
      case 'ViewDataElementRecordTaskInspectionResult': {
        return data.recordTaskInspectionResult?.name ?? ''
      }
      case 'ViewDataElementRecordTaskInspectionStatus': {
        return data.recordTaskInspectionStatus ?? ''
      }
      case 'ViewDataElementRecordTaskStatus': {
        return RecordTaskStatusTypeDisplayMap[data.recordTaskStatus]
      }
      case 'ViewDataElementRecordTaskType': {
        return RecordTaskTypeDisplayMap[data.recordTaskType]
      }
      case 'ViewDataElementFeeType': {
        return data.fee.name
      }
      case 'ViewDataElementGeneralLedgerNumberType': {
        return data.generalLedgerNumber.glNumber ?? ''
      }
      case 'ViewDataElementRecordTypeType': {
        return data.recordType.displayName
      }
      case 'ViewDataElementRecordTaskPaymentStatusType': {
        return data.recordTaskPaymentStatus
      }
      case 'ViewDataElementPaymentMethodType': {
        return formatPaymentMethod(data.paymentMethod, paymentProviderFormatted)
      }
      case 'ViewDataElementPaymentTransferType': {
        return data.displayLabel ?? TransferTypeGovwellToDisplayMap[data.paymentTransferType]
      }
      case 'ViewDataElementOrganizationFinixMerchantType': {
        return data.organizationFinixMerchant.description
      }
      case 'ViewDataElementRecordTypeApplicationGroup': {
        return data.recordTypeApplicationGroup.name
      }
      case 'ViewDataElementFileOnRecordType': {
        return FileOnRecordTypeDisplayMap[data.fileOnRecordType] ?? ''
      }
      case 'ViewDataElementSurveyResponseRating': {
        return data.surveyResponseRating
      }
      case 'ViewDataElementSurveyType': {
        return data.surveyType
      }
      default:
        return ''
    }
  }

  private _getIndexOfOption(option: string) {
    return this.optionsSelected?.findIndex((o) => o === option) ?? -1
  }

  public unselectOption(option: string) {
    const index = this._getIndexOfOption(option)
    if (index < 0) {
      return
    }
    const newOptionsSelected = this.optionsSelected.slice()
    newOptionsSelected.splice(index, 1)
    newOptionsSelected.sort()
    this.optionsSelected = newOptionsSelected
  }

  public selectOption(option: string) {
    const index = this._getIndexOfOption(option)
    if (index >= 0) {
      return
    }
    const newOptionsSelected = this.optionsSelected?.slice() ?? []
    newOptionsSelected.push(option)
    newOptionsSelected.sort()
    this.optionsSelected = newOptionsSelected
  }

  public reset() {
    this.setValues(this._databaseValue)
  }

  public save() {
    this._databaseValue = { ...toJS(this.protocol) }
  }

  public get protocol(): ViewFilterWithoutColumnType {
    return {
      columnTemplateGeneratedId: this.columnTemplateGeneratedId,
      bool: this.bool,
      date: toJS(this.date.protocol),
      isNull: this.isNull,
      membership:
        this.optionsSelected.length > 0
          ? {
              type: this.membershipType,
              optionsSelected: toJS(
                this.optionsSelected.filter(
                  (selectedOption) => !!this._getTemplateOptionForOptionId(selectedOption)
                )
              ),
            }
          : undefined,
      number:
        !isNil(this.number) && !isNil(this.numberComparison)
          ? toJS({ comparison: this.numberComparison, number: this.number })
          : undefined,
    }
  }

  public get databaseProtocol(): ViewFilterWithoutColumnType {
    return {
      ...this._databaseValue,
      date: TableViewDateFilter.isDatePopulated(this._databaseValue.date)
        ? toJS(this._databaseValue.date)
        : undefined,
      membership:
        this._databaseValue.membership && !!this._databaseValue.membership.optionsSelected?.length
          ? {
              type: this._databaseValue.membership.type,
              optionsSelected: toJS(this._databaseValue.membership.optionsSelected),
            }
          : undefined,
    }
  }
}
